import { ModalDialog } from '@/scripts/theme/modal-dialog'
import {
	getAttributeOrThrow,
	qsaOptional,
	qsOptional,
	qsRequired,
    qsaRequired,
    targetRequired,
	replaceAll,
} from '@/scripts/core/global'
import { type CartNotification } from '@/scripts/theme/cart-notification'
import { type uCoastWindow } from '@/scripts/setup'
import { type CartDrawer } from '@/scripts/cart/cart-drawer'
import { type PickupAvailability } from '@/scripts/optional/pickup-availability'
import { type ProductModal } from '@/scripts/product/product-modal'
import { type VariantSelects } from '@/scripts/theme/variant-selects'
import { type VariantRadios } from '@/scripts/theme/variant-radios'
import { type ProductInfo } from '@/scripts/product/product-info'
import { ATTRIBUTES, SELECTORS } from '@/scripts/core/global';
import {mediaLoader} from "@/scripts/core/global";
import { UcoastEl } from '@/scripts/core/UcoastEl'

declare let window: uCoastWindow

export class LookBook extends UcoastEl {
    static htmlSelector = 'look-book'
    trigger: HTMLButtonElement
    container: HTMLElement
    popup: HTMLElement
    popupContainer: HTMLElement
    productElement?: HTMLElement
    constructor() {
        super()

        this.trigger = qsRequired('.lookbook-trigger', this)
        this.container = qsRequired('.lookbook-content', this)
        this.popup = document.querySelector('.lookbook-popup') as HTMLElement
        this.popupContainer = document.querySelector('.lookbook-popup--container') as HTMLElement
        this.trigger.addEventListener('click', this.onToggleLookbook.bind(this))

        document.body.addEventListener('click', this.closeLookbook.bind(this))
    }

    closeLookbook(event: Event) {
        const target = targetRequired<Event, HTMLInputElement>(event)
        if(target.closest('look-book') || target.closest('.lookbook-popup')) return

        this.classList.remove('active')
        this.popup.classList.remove('active')
        this.popupContainer.innerHTML = ''
        qsRequired('.lookbook-content', this).innerHTML = ''
    }

    onToggleLookbook() {
        const opened = this.classList.contains('active')
        const lookbooks = qsaRequired('look-book', document)
        lookbooks.forEach(lookbook => {
            lookbook.classList.remove('active')
            const container = qsRequired('.lookbook-content', lookbook)
            container.innerHTML = ''
        })
        this.popup.classList.remove('active')
        this.popupContainer.innerHTML = ''

        if(!opened) {
            this.trigger.setAttribute('aria-disabled', 'true')
            this.trigger.setAttribute(ATTRIBUTES.loading, '')
            qsRequired(SELECTORS.loadingOverlaySpinner, this.trigger).classList.remove('hidden')

            const className = [...this.container.classList].find(className => className.indexOf('color-') !== -1) || '';
            this.popup.classList.add(className)

            this.getProduct(getAttributeOrThrow('data-product-url', this))
        }
    }

    getProduct(url: string) {
        fetch(url)
            .then((response) => response.text())
            .then((responseText) => {
                const responseHTML = new DOMParser().parseFromString(responseText, 'text/html')
                this.productElement = qsRequired(
                    'section[id^="MainProduct-"]',
                    responseHTML.documentElement
                )
                this.preventDuplicatedIDs()
                this.removeDOMElements()
                const container = window.innerWidth > 768? this.container: this.popupContainer
                this.setInnerHTML(container, this.productElement.innerHTML)
                mediaLoader()

                if (window.Shopify && window.Shopify.PaymentButton) {
                    window.Shopify.PaymentButton.init()
                }

                if (window.ProductModel) window.ProductModel.loadShopifyXR()

                this.removeGalleryListSemantic()
                this.updateImageSizes()
                this.preventVariantURLSwitching()

                const _this = this

                const swatches = container.querySelectorAll('.product-form__input--swatches a')
                swatches.forEach(swatch => {
                    const _swatch = swatch as HTMLAnchorElement
                    swatch.addEventListener('click', function(e){
                        e.preventDefault()
                        _this.getProduct(getAttributeOrThrow('href', _swatch))
                    })
                })
            })
            .finally(() => {
                this.classList.add('active')
                this.popup.classList.add('active')

                this.trigger.removeAttribute('aria-disabled')
				this.trigger.removeAttribute(ATTRIBUTES.loading)
				qsRequired(SELECTORS.loadingOverlaySpinner, this.trigger).classList.add('hidden')
            })
    }

    setInnerHTML(element: HTMLElement, html: string) {
            element.innerHTML = html
    
            // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
            element.querySelectorAll('script').forEach((oldScriptTag: HTMLScriptElement) => {
                if (!oldScriptTag.parentNode)
                    throw new Error('oldScriptTag.parentNode is null, cannot replace script')
                const newScriptTag = document.createElement('script')
                Array.from(oldScriptTag.attributes).forEach((attribute) => {
                    newScriptTag.setAttribute(attribute.name, attribute.value)
                })
                newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML))
                oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag)
            })
        }
    
        preventVariantURLSwitching() {
            const container = window.innerWidth > 768? this.container: this.popupContainer
            const variantPicker = container.querySelector('variant-radios,variant-selects')
            if (!variantPicker) return
    
            variantPicker.setAttribute('data-update-url', 'false')
        }
    
        removeDOMElements() {
            if (!this.productElement)
                throw new Error('this.removeDOMElements called without this.productElement')
            qsOptional<PickupAvailability>('pickup-availability', this.productElement)?.remove()
            qsOptional<ProductModal>('product-modal', this.productElement)?.remove()
            const modalDialogs = qsaOptional<ModalDialog>('modal-dialog', this.productElement)
            if (modalDialogs) {
                modalDialogs.forEach((modal) => modal.remove())
            }
        }
    
        preventDuplicatedIDs() {
            if (!this.productElement)
                throw new Error('this.preventDuplicatedIDs called without this.productElement')
            const sectionId = getAttributeOrThrow('data-section', this.productElement)
            this.productElement.innerHTML = replaceAll(
                this.productElement.innerHTML,
                sectionId,
                `quickadd-${sectionId}`
            )
            const formComponents = qsaOptional<VariantSelects | VariantRadios | ProductInfo>(
                'variant-selects, variant-radios, product-info',
                this.productElement
            )
            formComponents?.forEach((element) => {
                element.dataset.originalSection = sectionId
            })
        }
    
        removeGalleryListSemantic() {
            const container = window.innerWidth > 768? this.container: this.popupContainer
            const galleryList = container.querySelector('[id^="Slider-Gallery"]')
            if (!galleryList) return
    
            galleryList.setAttribute('role', 'presentation')
            galleryList
                .querySelectorAll('[id^="Slide-"]')
                .forEach((li) => li.setAttribute('role', 'presentation'))
        }
    
        updateImageSizes() {
            const container = window.innerWidth > 768? this.container: this.popupContainer
            const product = qsRequired('.product', container)
            const desktopColumns = product.classList.contains('product--columns')
            if (!desktopColumns) return
    
            const mediaImages = qsaOptional<HTMLImageElement>('.product__media img', product)
            if (!mediaImages) return
    
            let mediaImageSizes =
                '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)'
    
            if (product.classList.contains('product--medium')) {
                mediaImageSizes = mediaImageSizes.replace('715px', '605px')
            } else if (product.classList.contains('product--small')) {
                mediaImageSizes = mediaImageSizes.replace('715px', '495px')
            }
    
            mediaImages.forEach((img: HTMLImageElement) => img.setAttribute('sizes', mediaImageSizes))
        }
}
